/**
 * Avatar de Androcles.
 *
 * Modo pleno: recibe frames de MuseTalk por WebSocket (avatar_service, :8301)
 * y los pinta en un <canvas> sincronizados con el TTS.
 *
 * MODO DEGRADADO (obligatorio, seccion 4quater de la especificacion):
 * si el servicio cae, no conecta, o el servidor pide degradar explicitamente,
 * se conmuta a retrato estatico con indicador de habla. La conversacion nunca
 * se interrumpe: la voz jamas depende del video.
 *
 * IMPORTANTE (26/07/2026): el avatar YA NO degrada por fps. MuseTalk genera a
 * ~2.9x mas lento que tiempo real; medir fps y degradar por debajo de un minimo
 * tiraba la cara al medallon sin motivo real. Ahora solo se degrada ante FALLO
 * del socket (onerror/onclose) o `{"tipo":"degradado"}` explicito del servidor.
 * La cara en reposo se mantiene aunque vaya a pocos fps.
 *
 * REPRODUCCION POR RESPUESTA ENTERA (26/07/2026): en vez de reproducir frase a
 * frase (que congelaba la cara entre frases mientras se generaba la siguiente),
 * se ACUMULA toda la respuesta —los frames de todas sus frases y sus WAVs— y,
 * solo cuando esta completa y con todos los frames pre-decodificados, se
 * reproduce ENTERA de una vez: fluida, back-to-back y en sync. El precio es una
 * espera mas larga "pensando"; a cambio, cuando habla es suave.
 *
 * La conmutacion a degradado es de una sola direccion dentro de la sesion: una
 * vez degradado no se vuelve a intentar solo, para no producir parpadeos que
 * desconcierten al usuario. Se reintenta al abrir sesion nueva.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ErrorApi, urlWebSocket, type ConfigAvatar } from '@/lib/api';
import {
  descartarRespuestaAcumulada,
  modoSincronizado,
  observarAudio,
  reproducirRespuestaAcumulada,
} from '@/lib/reproductor';
import { cn } from '@/lib/utils';

/**
 * LIP-SYNC en tiempo real: APAGADO a proposito (26/07/2026).
 *
 * MuseTalk genera los frames de labios ~2.9x mas lento que tiempo real en la
 * RTX 3060. Se probaron las dos vias posibles y NINGUNA da un resultado bueno
 * en esta GPU (medido en produccion):
 *   - frase a frase -> tirones, congelados y repeticiones entre frases.
 *   - respuesta entera -> suave, pero 25 s+ de cara quieta "pensando" antes de
 *     hablar (hay que generar toda la respuesta antes de reproducir).
 * Con el lip-sync APAGADO, el avatar muestra la cara fotorrealista con su
 * micro-animacion de reposo (parpadeo/respiracion) SIEMPRE, mas un brillo suave
 * al hablar, y la VOZ suena inmediata y fluida. Es lo que se ve bien aqui.
 *
 * Para REACTIVAR el lip-sync (con una GPU mas potente): poner `true`. El motor
 * MuseTalk sigue instalado y todo el camino (acumulacion, sincronia, lotes)
 * queda intacto detras de este flag.
 */
const LIP_SYNC = false;

/** Traduce el estado visible del kiosco al vocabulario del protocolo /stream. */
function estadoProtocolo(estado: EstadoAvatar): 'escucha' | 'habla' | 'espera' {
  if (estado === 'hablando') return 'habla';
  if (estado === 'escuchando') return 'escucha';
  // 'pensando' e 'inactivo' -> espera: parpadeo y respiracion, sin lip-sync.
  return 'espera';
}

/** Estado visible del avatar; gobierna la animacion del retrato. */
export type EstadoAvatar = 'inactivo' | 'escuchando' | 'pensando' | 'hablando';

interface PropsAvatar {
  estado: EstadoAvatar;
  /** Fuerza el retrato estatico sin intentar conectar (pruebas, o VRAM justa). */
  forzarDegradado?: boolean;
  /**
   * Hay sesion abierta. El kiosco sigue montado DEBAJO de la pantalla de
   * entrada, asi que sin este freno el avatar reintentaba `/api/voz/estado`
   * cada 2 s contra un backend que solo puede contestar 401 (visto el
   * 04/08/2026 en el log: cientos de 401 seguidos con la sesion del jefe ya
   * caducada). Con la sesion cerrada no se pregunta; al volver a entrar, el
   * cambio de esta prop relanza el intento solo.
   */
  sesionActiva?: boolean;
  className?: string;
}

/**
 * Margen sin nuevos audios que marca "ya han llegado todas las frases de la
 * respuesta" (ms). Los WAVs (Piper TTS) llegan rapido y en rafaga; un hueco de
 * este tamano sin mas audios significa que la respuesta no tiene mas frases.
 */
const MARGEN_FIN_AUDIO = 800;

/**
 * Plazo de seguridad de la respuesta (ms). Se re-arma con cada actividad (WAV o
 * lote). Si pasa este tiempo SIN actividad y la respuesta no se ha cerrado (el
 * avatar va lento o degrada a mitad), se reproduce la voz acumulada IGUAL, sin
 * esperar mas a los frames: la voz es el minimo garantizado, nunca muda.
 */
const SEGURIDAD_RESPUESTA_MS = 15000;

export function Avatar({
  estado,
  forzarDegradado = false,
  sesionActiva = true,
  className,
}: PropsAvatar) {
  const [config, setConfig] = useState<ConfigAvatar | null>(null);
  const [degradado, setDegradado] = useState(true); // Se arranca degradado: es el estado seguro.
  const [motivoDegradado, setMotivoDegradado] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  /**
   * Socket del avatar en modo pleno. En una ref para que los efectos que
   * mandan el estado y derivan el audio lo usen sin reabrir la conexion.
   * `null` mientras no hay modo pleno (o ya se degrado).
   */
  const socketRef = useRef<WebSocket | null>(null);

  // --- ACUMULACION DE LA RESPUESTA ENTERA (modo MuseTalk) ----------------
  // MuseTalk genera los frames de lip-sync ~2.9x mas lento que tiempo real. Si
  // se reproduce frase a frase, entre frase y frase la cara se congela mientras
  // se genera la siguiente. Solucion: ACUMULAR toda la respuesta (los frames de
  // todas sus frases + sus WAVs) y, solo cuando esta COMPLETA y con los frames
  // pre-decodificados, reproducirla ENTERA de una vez. Las estructuras van en
  // refs: viven entre renders y las comparten el efecto del socket, el de
  // derivacion de audio y el de inicio de respuesta.

  /** Lote que se esta BUFFERANDO ahora (entre lote_inicio y lote_fin), o null. */
  const loteBufferRef = useRef<{ frames: ArrayBuffer[]; fps: number } | null>(null);
  /**
   * Lotes (frames de cada frase, en orden) ya cerrados y emparejados con su
   * WAV, acumulados para la RESPUESTA en curso. No se pintan hasta que la
   * respuesta esta completa.
   */
  const respuestaLotesRef = useRef<Array<{ frames: ArrayBuffer[]; fps: number }>>([]);
  /** true mientras se acumula una respuesta (entre su inicio y su reproduccion). */
  const acumulandoRef = useRef(false);
  /**
   * WAVs recibidos y AUN sin emparejar con su lote (contador FIFO). Un lote sin
   * WAV que lo respalde se descarta; un WAV sin lote lo cubre la seguridad.
   */
  const wavsSinLoteRef = useRef(0);
  /** WAVs (frases de audio) recibidos en la respuesta en curso. */
  const frasesRecibidasRef = useRef(0);
  /** Total de frases de la respuesta; `null` hasta cerrar la ventana de audio. */
  const totalFrasesRef = useRef<number | null>(null);
  /** Timer de la ventana "sin nuevos audios" (fija cuantas frases hay). */
  const timerAudioGapRef = useRef<number | undefined>(undefined);
  /** Timer de seguridad de la respuesta (garantiza voz aunque el video falle). */
  const timerSeguridadRef = useRef<number | undefined>(undefined);

  /** true mientras se esta pintando la respuesta a ritmo fijo. */
  const reproduciendoLoteRef = useRef(false);
  /** id del requestAnimationFrame que pinta la respuesta en curso. */
  const rafLoteRef = useRef<number | undefined>(undefined);
  /** Bitmaps YA decodificados de la respuesta en curso (para cerrarlos al fin). */
  const bitmapsLoteRef = useRef<ImageBitmap[]>([]);
  /** true si se pidio abortar la pintura en curso (degradado/cierre/interrupcion). */
  const abortarLoteRef = useRef(false);
  /** Estado anterior, para detectar la TRANSICION a 'pensando' (inicio de respuesta). */
  const estadoPrevioRef = useRef<EstadoAvatar>(estado);

  /** Cancela los temporizadores de la acumulacion (ventana de audio + seguridad). */
  const limpiarTimersAcumulacion = () => {
    if (timerAudioGapRef.current !== undefined) {
      window.clearTimeout(timerAudioGapRef.current);
      timerAudioGapRef.current = undefined;
    }
    if (timerSeguridadRef.current !== undefined) {
      window.clearTimeout(timerSeguridadRef.current);
      timerSeguridadRef.current = undefined;
    }
  };

  /** Deja la acumulacion a cero (sin tocar la pintura en curso). */
  const resetAcumulacion = () => {
    limpiarTimersAcumulacion();
    respuestaLotesRef.current = [];
    loteBufferRef.current = null;
    wavsSinLoteRef.current = 0;
    frasesRecibidasRef.current = 0;
    totalFrasesRef.current = null;
    acumulandoRef.current = false;
  };

  /**
   * Aborta la pintura de la respuesta en curso y libera sus bitmaps.
   *
   * Se usa al degradar, cerrar o empezar una respuesta nueva que interrumpe a
   * otra que aun sonaba.
   */
  const abortarReproduccion = () => {
    abortarLoteRef.current = true;
    if (rafLoteRef.current !== undefined) {
      window.cancelAnimationFrame(rafLoteRef.current);
      rafLoteRef.current = undefined;
    }
    bitmapsLoteRef.current.forEach((b) => b.close());
    bitmapsLoteRef.current = [];
    reproduciendoLoteRef.current = false;
  };

  /**
   * Re-arma el temporizador de seguridad de la respuesta.
   *
   * Se llama con cada actividad (WAV o lote). Si pasa `SEGURIDAD_RESPUESTA_MS`
   * SIN actividad y la respuesta sigue acumulandose (avatar lento o degradando
   * a mitad), se reproduce la voz acumulada IGUAL, sin esperar mas a los frames.
   * La voz nunca se queda muda.
   */
  const rearmarSeguridad = () => {
    if (timerSeguridadRef.current !== undefined) window.clearTimeout(timerSeguridadRef.current);
    timerSeguridadRef.current = window.setTimeout(() => {
      timerSeguridadRef.current = undefined;
      if (!acumulandoRef.current) return;
      // La respuesta no se cerro a tiempo: se suelta la VOZ con lo acumulado y
      // se deja de esperar a los frames (la cara sigue en reposo). El texto ya
      // se ve; la voz es el minimo garantizado.
      console.warn('[avatar] respuesta sin cerrar a tiempo; se reproduce solo la voz');
      resetAcumulacion();
      reproducirRespuestaAcumulada();
    }, SEGURIDAD_RESPUESTA_MS);
  };

  /**
   * Empieza a acumular una respuesta si no se estaba ya (arranque perezoso al
   * llegar el primer WAV). No pisa una acumulacion en curso.
   */
  const empezarAcumulacion = () => {
    if (acumulandoRef.current) return;
    resetAcumulacion();
    acumulandoRef.current = true;
    rearmarSeguridad();
  };

  /**
   * Inicia una respuesta NUEVA (llega `estado_agente:'pensando'`), descartando
   * cualquier resto de una respuesta anterior interrumpida.
   */
  const iniciarRespuesta = () => {
    abortarReproduccion();
    resetAcumulacion();
    descartarRespuestaAcumulada(); // por si quedaban WAVs de una respuesta interrumpida
    acumulandoRef.current = true;
    rearmarSeguridad();
  };

  /** Descarta todo el estado de la acumulacion y la pintura (al degradar/cerrar). */
  const limpiarLotes = () => {
    abortarReproduccion();
    resetAcumulacion();
  };

  /**
   * Reproduce la respuesta ENTERA: pre-decodifica TODOS los frames de todas sus
   * frases a `ImageBitmap`, arranca la voz acumulada (back-to-back, sin huecos)
   * y pinta los frames a fps constante guiado por el reloj real.
   *
   * CLAVE para que NO haya tirones en el movil: los frames se PRE-DECODIFICAN
   * todos ANTES de empezar; luego la reproduccion es solo `drawImage` de bitmaps
   * ya listos, y el fotograma se elige por TIEMPO transcurrido (no por contador),
   * asi que no se acumula retraso aunque el navegador vaya justo.
   *
   * Como cada lote tiene frames = duracion_wav * fps y la voz suena back-to-back,
   * el total cuadra: el frame k corresponde al instante de audio acumulado.
   */
  const reproducirRespuestaCompleta = () => {
    if (reproduciendoLoteRef.current) return; // defensivo: ya sonando
    const lotes = respuestaLotesRef.current;
    // Cierra la acumulacion: la siguiente respuesta empezara limpia y ningun
    // lote se reproduce dos veces.
    resetAcumulacion();

    // Concatena, en orden, los frames de todas las frases.
    const fps = lotes[0]?.fps || 25;
    const todos: ArrayBuffer[] = [];
    for (const lote of lotes) for (const f of lote.frames) todos.push(f);

    if (todos.length === 0) {
      // Respuesta sin frames utiles: al menos la voz suena.
      reproducirRespuestaAcumulada();
      return;
    }

    reproduciendoLoteRef.current = true;
    abortarLoteRef.current = false;

    Promise.all(
      todos.map((datos) =>
        createImageBitmap(new Blob([datos], { type: 'image/jpeg' })).catch(() => null),
      ),
    ).then((bitmaps) => {
      const utiles = bitmaps.filter((b): b is ImageBitmap => b !== null);
      if (abortarLoteRef.current || utiles.length === 0) {
        utiles.forEach((b) => b.close());
        reproduciendoLoteRef.current = false;
        // Aun asi, garantizar la voz: nunca muda por un fallo de decodificacion.
        reproducirRespuestaAcumulada();
        return;
      }
      bitmapsLoteRef.current = utiles;

      // Voz y video ARRANCAN JUNTOS: la respuesta entera suena back-to-back y
      // los frames se pintan a fps constante desde este mismo instante.
      reproducirRespuestaAcumulada();

      const lienzo = canvasRef.current;
      const ctx = lienzo?.getContext('2d') ?? null;
      let inicio: number | null = null;

      const paso = (t: number) => {
        if (abortarLoteRef.current) return;
        if (inicio === null) inicio = t;
        // El fotograma se elige por TIEMPO real: nada de acumular retraso.
        const idx = Math.floor(((t - inicio) / 1000) * fps);
        if (idx >= utiles.length) {
          // Fin de la respuesta: se cierran los bitmaps y se vuelve a reposo.
          utiles.forEach((b) => b.close());
          bitmapsLoteRef.current = [];
          rafLoteRef.current = undefined;
          reproduciendoLoteRef.current = false;
          return;
        }
        if (ctx && lienzo) ctx.drawImage(utiles[idx], 0, 0, lienzo.width, lienzo.height);
        rafLoteRef.current = window.requestAnimationFrame(paso);
      };
      rafLoteRef.current = window.requestAnimationFrame(paso);
    });
  };

  /**
   * Comprueba si la respuesta esta COMPLETA y, si lo esta, la reproduce entera.
   *
   * Completa = ya se sabe cuantas frases tiene (paso la ventana sin nuevos
   * audios) Y han llegado los lotes de todas ellas.
   */
  const comprobarRespuestaCompleta = () => {
    if (!acumulandoRef.current) return;
    const total = totalFrasesRef.current;
    if (total === null) return; // aun pueden llegar mas frases de audio
    if (respuestaLotesRef.current.length < total) return; // faltan lotes
    reproducirRespuestaCompleta();
  };

  // 1) Leer la configuracion publicada por el backend (settings.ini -> [avatar]).
  //    REINTENTA: al montar, la sesion puede no estar lista todavia (el Avatar
  //    vive en el armazon y se pinta antes del login), y `/api/voz/estado`
  //    devolveria 401 -> el avatar se quedaba degradado PARA SIEMPRE aunque el
  //    servicio funcionase (reporte del jefe 25/07/2026: "desaparecio"). Se
  //    reintenta cada 2 s hasta lograrlo; en cuanto hay sesion, la config llega
  //    y el avatar conecta.
  useEffect(() => {
    // Sin sesion no se pregunta: solo saldrian 401 en bucle (ver `sesionActiva`).
    if (!sesionActiva) return;
    let vivo = true;
    let timer: number | undefined;
    const intentar = () => {
      api
        .configAvatar()
        .then((c) => {
          if (!vivo) return;
          setConfig(c);
          setMotivoDegradado(null);
        })
        .catch((error: unknown) => {
          if (!vivo) return;
          // 401 = sesion caducada mientras el kiosco seguia abierto. Quien
          // arregla eso es la pantalla de entrada, no un reintento: se para
          // hasta que `sesionActiva` vuelva a ser cierto.
          if (error instanceof ErrorApi && error.esSesionCaducada) {
            setMotivoDegradado(null);
            return;
          }
          setMotivoDegradado('Aun no puedo conectar con el avatar; reintentando…');
          timer = window.setTimeout(intentar, 2000);
        });
    };
    intentar();
    return () => {
      vivo = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [sesionActiva]);

  // 2) Intentar el modo pleno solo si procede.
  useEffect(() => {
    const forzado =
      forzarDegradado || import.meta.env.VITE_AVATAR_FORZAR_DEGRADADO === 'true';
    if (!config || !config.activo || forzado) {
      setDegradado(true);
      if (forzado) setMotivoDegradado('Modo degradado forzado por configuracion');
      else if (config && !config.activo) setMotivoDegradado('Avatar desactivado en settings.ini');
      return;
    }

    let socket: WebSocket | null = null;
    /** true mientras haya un frame en proceso de decodificar (evita cola). */
    let decodificando = false;

    /** Conmuta a retrato estatico y suelta el socket. Irreversible en la sesion. */
    const degradar = (motivo: string) => {
      setDegradado(true);
      setMotivoDegradado(motivo);
      // Volver a voz inmediata: se vuelca la respuesta acumulada (no queda muda)
      // y se tira el buffer de frames a medias.
      modoSincronizado(false);
      limpiarLotes();
      socket?.close();
      socket = null;
      socketRef.current = null;
    };

    /**
     * Pinta un frame JPEG/WebP de reposo recibido en el <canvas> de 512x512.
     *
     * Se descarta el frame si ya hay otro decodificandose: mas vale saltar un
     * fotograma que acumular retraso (la regla de oro: el video nunca manda).
     * `createImageBitmap` decodifica fuera del hilo principal.
     */
    const pintarFrame = (datos: ArrayBuffer) => {
      const lienzo = canvasRef.current;
      if (!lienzo || decodificando) return;
      decodificando = true;
      const blob = new Blob([datos], { type: 'image/jpeg' });
      createImageBitmap(blob)
        .then((mapa) => {
          const ctx = lienzo.getContext('2d');
          if (ctx) ctx.drawImage(mapa, 0, 0, lienzo.width, lienzo.height);
          mapa.close();
        })
        .catch(() => {
          // Un frame ilegible se salta; no se degrada por uno suelto.
        })
        .finally(() => {
          decodificando = false;
        });
    };

    try {
      // Una ruta relativa (p. ej. "/avatar/ws") se resuelve contra el origen de
      // la pagina: asi por el dominio publico va a wss://androcles.ceslan.com/
      // avatar/ws (a traves del proxy) y en local a ws://127.0.0.1. Una URL
      // absoluta (ws://...) se usa tal cual, para desarrollo directo contra 8301.
      const destino = config.servicio_url.startsWith('/')
        ? urlWebSocket(config.servicio_url)
        : config.servicio_url;
      socket = new WebSocket(destino);
      socket.binaryType = 'arraybuffer';

      socket.onopen = () => {
        setDegradado(false);
        setMotivoDegradado(null);
        socketRef.current = socket;
        // Con lip-sync ON, la voz se acumula por respuesta (espera a que este
        // lista). Con lip-sync OFF (lo actual), la voz suena INMEDIATA: no se
        // entra en modo sincronizado.
        if (LIP_SYNC) modoSincronizado(true);
        // Al abrir, se anuncia el estado actual para que la cara arranque bien.
        try {
          socket?.send(JSON.stringify({ tipo: 'estado', valor: estadoProtocolo(estado) }));
        } catch {
          // Si el envio falla, el onerror/onclose se encarga de degradar.
        }
        // NOTA (26/07/2026): se QUITO la vigilancia de fps que degradaba al
        // retrato si no se sostenia un minimo. MuseTalk va a ~2.9x y esa medida
        // tiraba la cara al medallon sin motivo real. Solo se degrada ante FALLO
        // del socket (onerror/onclose) o `{"tipo":"degradado"}` del servidor.
      };

      socket.onmessage = (evento) => {
        // Frame binario. Segun la fase:
        //  - dentro de un lote (entre lote_inicio y lote_fin): se BUFFEREA para
        //    la respuesta; no se pinta aun.
        //  - mientras se pinta una respuesta: los frames de reposo sueltos se
        //    IGNORAN, para no pisar el lip-sync en curso.
        //  - en reposo (sin lote y sin reproduccion, incluida la fase de
        //    "pensando"/acumulacion): se pinta al vuelo, para que la cara siga
        //    viva mientras se genera la respuesta.
        if (typeof evento.data !== 'string') {
          if (evento.data instanceof ArrayBuffer) {
            if (loteBufferRef.current) {
              loteBufferRef.current.frames.push(evento.data);
            } else if (!reproduciendoLoteRef.current) {
              pintarFrame(evento.data);
            }
          }
          return;
        }
        // Mensaje de control JSON (lote / metricas / orden de degradar).
        try {
          const msg = JSON.parse(evento.data) as {
            tipo?: string;
            motivo?: string;
            frames?: number;
            fps?: number;
          };
          if (msg.tipo === 'lote_inicio') {
            // Empieza el lote de una frase: se buffera hasta lote_fin.
            loteBufferRef.current = { frames: [], fps: msg.fps ?? 25 };
          } else if (msg.tipo === 'lote_fin') {
            // Lote completo: se empareja con el WAV mas antiguo sin lote y se
            // ACUMULA para la respuesta (no se reproduce todavia).
            const buf = loteBufferRef.current;
            loteBufferRef.current = null;
            if (!buf) return;
            if (!acumulandoRef.current || wavsSinLoteRef.current <= 0) {
              // Lote sin WAV que lo respalde (o fuera de una respuesta): se
              // DESCARTA, no se repite ni se pinta suelto.
              console.warn('[avatar] lote sin WAV emparejado; descartado');
              return;
            }
            wavsSinLoteRef.current -= 1;
            respuestaLotesRef.current.push({ frames: buf.frames, fps: buf.fps });
            rearmarSeguridad();
            comprobarRespuestaCompleta();
          } else if (msg.tipo === 'degradado') {
            degradar(msg.motivo ?? 'El servidor de avatar ha pedido degradar');
          }
          // 'metricas': informativo; ya no se usa para decidir degradar.
        } catch {
          // Mensaje ilegible: se ignora, no corta el video.
        }
      };

      socket.onerror = () => degradar('No se ha podido hablar con el servicio de avatar');
      socket.onclose = () => degradar('El servicio de avatar se ha desconectado');
    } catch {
      degradar('No se ha podido abrir la conexion con el avatar');
    }

    return () => {
      // Al desmontar/reabrir: voz inmediata otra vez y buffer descartado. El
      // volcado de `modoSincronizado(false)` evita voz muda.
      modoSincronizado(false);
      limpiarLotes();
      socket?.close();
      socketRef.current = null;
    };
    // `estado` se lee solo al abrir (se manda el inicial); su seguimiento vive
    // en un efecto aparte para no reabrir el socket en cada cambio de estado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, forzarDegradado]);

  // 3) Seguir el estado del kiosco: cada cambio se anuncia al servicio para
  //    que la cara asienta (escucha), mueva la boca (habla) o repose (espera).
  useEffect(() => {
    if (degradado) return;
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ tipo: 'estado', valor: estadoProtocolo(estado) }));
    } catch {
      // El propio socket disparara onerror/onclose si de verdad esta caido.
    }
  }, [estado, degradado]);

  // 3-bis) INICIO de respuesta: cuando el agente empieza a "pensar" arranca una
  //    acumulacion NUEVA (y descarta cualquier resto de una respuesta anterior
  //    interrumpida). La cara queda en reposo/pensando mientras se acumula; los
  //    frames de la respuesta no se pintan hasta que esta completa.
  useEffect(() => {
    const previo = estadoPrevioRef.current;
    estadoPrevioRef.current = estado;
    if (degradado) return;
    // Solo la TRANSICION a 'pensando' arranca una respuesta nueva; asi un
    // re-render con el mismo estado no reinicia una acumulacion en curso.
    if (estado === 'pensando' && previo !== 'pensando') iniciarRespuesta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, degradado]);

  // 4) Derivar al avatar el MISMO audio TTS que suena, para el lip-sync. El
  //    reproductor avisa con cada WAV encolado; se reenvia por el socket y se
  //    CUENTA como una frase de la respuesta en curso. Si el socket no esta, no
  //    pasa nada: la voz es independiente del avatar.
  useEffect(() => {
    // Con lip-sync OFF no se deriva el audio al avatar: la cara se queda en
    // reposo (parpadeo/respiracion) y la voz suena aparte, inmediata. Sin esto
    // el avatar intentaria generar labios que aqui no queremos.
    if (!LIP_SYNC) return;
    return observarAudio((wav) => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      // Arranque perezoso (por si no llego 'pensando') y conteo de la frase.
      empezarAcumulacion();
      frasesRecibidasRef.current += 1;
      wavsSinLoteRef.current += 1;
      rearmarSeguridad();
      // Ventana "sin nuevos audios": cada WAV la reinicia. Cuando pasa el margen
      // sin mas audios, se fija cuantas frases tiene la respuesta y se comprueba
      // si ya estan todos sus lotes (los WAVs llegan en rafaga; los lotes, mas
      // lentos, van completando despues).
      totalFrasesRef.current = null;
      if (timerAudioGapRef.current !== undefined) window.clearTimeout(timerAudioGapRef.current);
      timerAudioGapRef.current = window.setTimeout(() => {
        timerAudioGapRef.current = undefined;
        totalFrasesRef.current = frasesRecibidasRef.current;
        comprobarRespuestaCompleta();
      }, MARGEN_FIN_AUDIO);
      try {
        // El backend procesa los WAVs en el orden en que se envian; asi los
        // lotes que devuelve conservan ese orden y se emparejan por FIFO.
        socket.send(wav);
      } catch {
        // Un fallo de envio al avatar jamas afecta a la reproduccion de voz.
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hablando = estado === 'hablando';

  // Retrato definitivo publicado por el backend, si lo hay. El SVG de stock
  // (silueta generica) NO se usa: quedaba como un icono a medio terminar. En
  // su ausencia se pinta un marcador de presencia disenado (ver Presencia).
  const retratoReal = config?.retrato_url || null;

  return (
    <div
      className={cn(
        // Movil: presencia contenida (16:10) para no comerse la pantalla antes
        // de llegar al parte y al chat. Escritorio: retrato alto en la columna
        // lateral, donde el espacio sobra.
        'relative aspect-[16/10] w-full overflow-hidden rounded-lg border border-border/70 bg-card sm:aspect-[4/3] lg:aspect-[5/6]',
        className,
      )}
      // Region informativa, no interactiva: se anuncia el estado, no el video.
      role="img"
      aria-label={etiquetaAccesible(estado, degradado)}
    >
      {/* Modo pleno: lienzo de MuseTalk.
          Con LIP_SYNC apagado el servicio manda un frame de reposo ESTATICO
          (misma foto siempre), asi que se le da vida por CSS: un balanceo y
          respiracion muy suaves para que la cara real no parezca congelada.
          Al hablar sigue vivo igual (el brillo/onda marcan que habla). */}
      <canvas
        ref={canvasRef}
        className={cn(
          'h-full w-full object-cover',
          !LIP_SYNC && !degradado && 'animate-vida-cara',
          degradado && 'hidden',
        )}
        width={512}
        height={512}
      />

      {/* Modo degradado: retrato real si existe, o marcador de presencia. */}
      {degradado &&
        (retratoReal ? (
          <img
            src={retratoReal}
            alt=""
            className={cn(
              'h-full w-full object-cover',
              estado === 'inactivo' && 'animate-respiracion',
            )}
          />
        ) : (
          <Presencia estado={estado} />
        ))}

      {/* Brillo suave al hablar: con el lip-sync apagado, es la senal principal
          de que la cara "esta hablando". Un anillo interior de acento que
          respira, sin tapar la cara. */}
      {hablando && (
        <div
          className="pointer-events-none absolute inset-0 rounded-lg shadow-[inset_0_0_60px_-10px_hsl(var(--accent))] ring-2 ring-inset ring-accent/50 animate-respiracion"
          aria-hidden="true"
        />
      )}

      {/* Indicador de habla: obligatorio en degradado, util tambien en pleno. */}
      {hablando && <OndaDeVoz />}

      <IndicadorEstado estado={estado} degradado={degradado} motivo={motivoDegradado} />
    </div>
  );
}

/**
 * Marcador de presencia para cuando aun no hay retrato real.
 *
 * Sustituye a la silueta de stock (que parecia un icono sin terminar) por un
 * medallon con la inicial de Androcles sobre un halo de marca. Respira despacio
 * en reposo y se ilumina un punto al escuchar/hablar: comunica "estoy aqui y
 * atento" sin fingir una cara que todavia no existe.
 */
function Presencia({ estado }: { estado: EstadoAvatar }) {
  const atento = estado === 'escuchando' || estado === 'hablando' || estado === 'pensando';
  return (
    <div className="absolute inset-0 grid place-items-center bg-[radial-gradient(120%_120%_at_50%_25%,hsl(var(--primary)/0.22),transparent_60%)]">
      <div
        className={cn(
          'grid h-[42%] max-h-44 min-h-28 w-auto aspect-square place-items-center rounded-full',
          'border border-primary/30 bg-gradient-to-b from-primary/25 to-primary/5',
          'shadow-[0_0_60px_-12px_hsl(var(--primary)/0.6)]',
          estado === 'inactivo' && 'animate-respiracion',
        )}
      >
        <span className="text-6xl font-semibold text-foreground/90" aria-hidden="true">
          A
        </span>
      </div>
      <span
        className={cn(
          'absolute bottom-[14%] h-2.5 w-2.5 rounded-full transition-colors duration-300',
          atento ? 'bg-accent shadow-[0_0_12px_hsl(var(--accent))]' : 'bg-muted-foreground/40',
        )}
        aria-hidden="true"
      />
    </div>
  );
}

/** Barras animadas que sustituyen al lip-sync cuando no hay video. */
function OndaDeVoz() {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 flex h-24 items-end
                 justify-center gap-2 bg-gradient-to-t from-background/90 to-transparent pb-5"
      aria-hidden="true"
    >
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <span
          key={i}
          className="w-3 origin-bottom rounded-full bg-accent animate-onda-voz"
          style={{ height: '3rem', animationDelay: `${i * 0.11}s` }}
        />
      ))}
    </div>
  );
}

/** Cinta superior con el estado en palabras: el usuario no interpreta iconos. */
function IndicadorEstado({
  estado,
  degradado,
  motivo,
}: {
  estado: EstadoAvatar;
  degradado: boolean;
  motivo: string | null;
}) {
  const texto: Record<EstadoAvatar, string> = {
    inactivo: 'Aquí estoy',
    escuchando: 'Le escucho',
    pensando: 'Estoy mirandolo',
    hablando: 'Hablando',
  };

  return (
    <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-3 bg-gradient-to-b from-background/90 to-transparent px-4 pb-6 pt-3">
      <span className="flex items-center gap-2 text-base font-medium">
        <span
          className="h-2 w-2 rounded-full bg-cobro shadow-[0_0_8px_hsl(var(--cobro))]"
          aria-hidden="true"
        />
        {texto[estado]}
      </span>
      {degradado && (
        // Se avisa a Ricardo, no se alarma al jefe: texto neutro y discreto.
        <span className="text-sm text-muted-foreground" title={motivo ?? undefined}>
          retrato
        </span>
      )}
    </div>
  );
}

function etiquetaAccesible(estado: EstadoAvatar, degradado: boolean): string {
  const base = `Androcles, estado: ${estado}`;
  return degradado ? `${base} (retrato estatico)` : base;
}
